import { get } from "@src/appV2/api/parsedApi";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { ShiftCancellationReasonTypes } from "@src/appV2/ShiftLogs/api/ShiftCancellationReasonTypes";
import {
  getFacilityDayShiftsResponseSchema,
  type ShiftWithAdjustments,
} from "@src/appV2/Shifts/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// eslint-disable-next-line no-restricted-imports -- following pattern from src/containers/postShifts/utils.ts
import { addDays, format, subDays } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- following pattern from src/containers/postShifts/utils.ts
import { zonedTimeToUtc } from "date-fns-tz";
import { useCallback, useEffect } from "react";
import { z } from "zod";

import { mapShiftsToCards, type ShiftCardData } from "../utils/mapShiftToCard";
import { type SentHomeInfo } from "../utils/parseSentHome";

const getFacilityDayShiftsQueryParamsSchema = z.object({
  dateFilter: z.object({
    start: z.string(),
    end: z.string(),
  }),
  tmz: z.string().optional(),
  facilityId: z.string(),
  includeWorkerLeftEarlyRequests: z.boolean(),
  includeProfileImageSizes: z.boolean().optional(),
  includePricingSettings: z.boolean().optional(),
  type: z.string().optional(),
});

import { GET_FACILITY_DAY_SHIFTS_URL } from "./queryKeys";

interface UseGetDayShiftsParams {
  date: Date;
  agentReq?: string;
}

interface UseGetDayShiftsOptions {
  enabled?: boolean;
}

interface CreateDayShiftsQueryOptionsParams {
  date: Date;
  facilityId: string;
  timezone?: string;
  agentReq?: string;
}

export interface DayShiftsResult {
  shifts: ShiftCardData[];
  date: string;
  canceledShifts: ShiftCardData[];
}

/**
 * Builds a map of shift ID to sent-home info from shift data.
 * Only includes shifts where `leftWithPermission` is true (worker was sent home by facility).
 * Shifts where `leftWithPermission` is false should use the WORKER_LEFT_EARLY state instead.
 */
function buildSentHomeMap(shifts: readonly ShiftWithAdjustments[]): Map<string, SentHomeInfo> {
  const sentHomeMap = new Map<string, SentHomeInfo>();

  for (const shift of shifts) {
    if (!shift.workerLeftEarly?.leftWithPermission) {
      continue;
    }

    sentHomeMap.set(shift._id, {
      shiftId: shift._id,
      reason: ShiftCancellationReasonTypes.SENT_HOME_BY_FACILITY,
      eventAt: undefined,
    });
  }

  return sentHomeMap;
}

/**
 * Builds a map of worker ID to worker name from shift data.
 */
function buildWorkerNamesMap(shifts: readonly ShiftWithAdjustments[]): Map<string, string> {
  const workerNamesMap = new Map<string, string>();

  for (const shift of shifts) {
    const workerId = shift.agent?.userId;
    const workerName = shift.agent?.name;

    if (workerId && workerName) {
      workerNamesMap.set(workerId, workerName);
    }
  }

  return workerNamesMap;
}

/**
 * Builds a map of worker ID to profile image URL from shift data.
 */
function buildWorkerProfileImagesMap(shifts: readonly ShiftWithAdjustments[]): Map<string, string> {
  const workerProfileImagesMap = new Map<string, string>();

  for (const shift of shifts) {
    const workerId = shift.agent?.userId;
    const profileImageUrl = shift.agent?.profileImageUrl;

    if (workerId && profileImageUrl) {
      workerProfileImagesMap.set(workerId, profileImageUrl);
    }
  }

  return workerProfileImagesMap;
}

/**
 * Transforms calendar/facility API shifts to ShiftCardData format.
 * Returns both active shifts and canceled shifts separately.
 */
function transformShiftsToCardData(
  shifts: readonly ShiftWithAdjustments[],
  timezone?: string
): { activeShifts: ShiftCardData[]; canceledShifts: ShiftCardData[] } {
  // Separate active and canceled (deleted + filled) shifts
  const activeShiftRecords = shifts.filter((shift) => shift.deleted !== true);
  const canceledShiftRecords = shifts.filter((shift) => shift.deleted === true && shift.agent);

  const sentHomeMap = buildSentHomeMap(activeShiftRecords);
  const workerNamesMap = buildWorkerNamesMap(activeShiftRecords);
  const workerProfileImagesMap = buildWorkerProfileImagesMap(activeShiftRecords);

  // Build maps for canceled shifts too
  const canceledWorkerNamesMap = buildWorkerNamesMap(canceledShiftRecords);
  const canceledWorkerProfileImagesMap = buildWorkerProfileImagesMap(canceledShiftRecords);

  return {
    activeShifts: mapShiftsToCards({
      shifts: activeShiftRecords,
      sentHomeMap,
      workerNamesMap,
      workerProfileImagesMap,
      timezone,
    }),
    canceledShifts: mapShiftsToCards({
      shifts: canceledShiftRecords,
      sentHomeMap: new Map(), // Canceled shifts don't have sent home info
      workerNamesMap: canceledWorkerNamesMap,
      workerProfileImagesMap: canceledWorkerProfileImagesMap,
      timezone,
    }),
  };
}

/**
 * Creates query key for day shifts.
 * Uses date format (yyyy-MM-dd) for consistent cache keys regardless of browser timezone.
 */
function createDayShiftsQueryKey(
  facilityId: string,
  date: Date,
  agentRequest?: string
): readonly unknown[] {
  const dateKey = format(date, "yyyy-MM-dd");
  return [GET_FACILITY_DAY_SHIFTS_URL, "dayShifts", facilityId, dateKey, agentRequest] as const;
}

async function fetchDayShifts(
  params: CreateDayShiftsQueryOptionsParams & { signal?: AbortSignal }
): Promise<DayShiftsResult> {
  const { date, facilityId, timezone, agentReq, signal } = params;
  const facilityTimezone = timezone ?? "UTC";

  // Extract calendar date components from the input date (browser timezone).
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const dateString = `${year}-${month}-${day}`;

  const dayStart = zonedTimeToUtc(`${dateString}T00:00:00`, facilityTimezone);
  const dayEnd = zonedTimeToUtc(`${dateString}T23:59:59.999`, facilityTimezone);

  const response = await get({
    url: GET_FACILITY_DAY_SHIFTS_URL,
    queryParamsSchema: getFacilityDayShiftsQueryParamsSchema,
    queryParams: {
      dateFilter: {
        start: dayStart.toISOString(),
        end: dayEnd.toISOString(),
      },
      tmz: timezone,
      facilityId,
      includeWorkerLeftEarlyRequests: true,
      includeProfileImageSizes: true,
      includePricingSettings: true,
      type: agentReq,
    },
    responseSchema: getFacilityDayShiftsResponseSchema,
    signal,
  });

  const { activeShifts, canceledShifts } = transformShiftsToCardData(response.data, timezone);

  return {
    shifts: activeShifts,
    date: dayStart.toISOString(),
    canceledShifts,
  };
}

export function createDayShiftsQueryOptions(params: CreateDayShiftsQueryOptionsParams) {
  const { facilityId, date, agentReq } = params;

  return {
    queryKey: createDayShiftsQueryKey(facilityId, date, agentReq),
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<DayShiftsResult> =>
      fetchDayShifts({ ...params, signal }),
    staleTime: 60_000,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_FACILITY_DAY_SHIFTS_FAILURE,
    },
  };
}

/**
 * Hook to fetch shifts for a single day from the calendar/facility endpoint.
 * This is more efficient than fetching an entire month because it only collects
 * agent IDs for a single day, avoiding the expensive MongoDB aggregation with
 * potentially thousands of agent IDs.
 *
 * Also prefetches adjacent days (day-1 and day+1) for smooth swiping navigation.
 */
export function useGetDayShifts(
  params: UseGetDayShiftsParams,
  options: UseGetDayShiftsOptions = {}
) {
  const { date, agentReq } = params;
  const { enabled = true } = options;

  const { profile } = useSession();
  const facilityId = profile.userId;
  const timezone = profile.tmz;

  const queryClient = useQueryClient();

  const query = useQuery({
    ...createDayShiftsQueryOptions({ date, facilityId, timezone, agentReq }),
    enabled: enabled && Boolean(facilityId),
    refetchInterval: 60_000, // Refetch every 60 seconds to recover from any sync issues
  });

  // Prefetch adjacent days for smooth navigation
  const prefetchDay = useCallback(
    async (targetDate: Date) => {
      await queryClient.prefetchQuery({
        ...createDayShiftsQueryOptions({
          date: targetDate,
          facilityId,
          timezone,
          agentReq,
        }),
      });
    },
    [facilityId, timezone, agentReq, queryClient]
  );

  // Prefetch adjacent days when the current day loads
  useEffect(() => {
    if (!query.data || !facilityId) {
      return;
    }

    // Prefetch previous and next day
    void prefetchDay(subDays(date, 1));
    void prefetchDay(addDays(date, 1));
  }, [query.data, date, facilityId, prefetchDay]);

  // Function to get cached shifts for a specific day (useful for swiping)
  const getCachedDayShifts = useCallback(
    (targetDate: Date): ShiftCardData[] | undefined => {
      const cacheKey = createDayShiftsQueryKey(facilityId, targetDate, agentReq);
      const cachedData = queryClient.getQueryData<DayShiftsResult>(cacheKey);
      return cachedData?.shifts;
    },
    [facilityId, agentReq, queryClient]
  );

  return {
    ...query,
    shifts: query.data?.shifts ?? [],
    canceledShifts: query.data?.canceledShifts ?? [],
    prefetchDay,
    getCachedDayShifts,
  };
}
